import React, {useRef, useEffect, useState} from 'react'
import Link from 'next/link'
import Head from 'next/head'
import {useRouter} from 'next/router'
import {useTranslation} from 'next-i18next'
import {Document, EMPTY_DOCUMENT} from '@contentful/rich-text-types'

import {useServicesContext} from '@miro-site/services'
import {Datalayer} from '@miro-site/analytics'
import {
  TemplateLoader,
  TemplateOpener,
  Box,
  Container,
  Heading,
  LazyImageProps,
} from '@miro-site/core'

import {
  CardGrid,
  RichText,
  TemplateCard,
  Template,
  TemplateCardProps,
  FaqItemsProps,
  FaqItem,
  ExternalLink,
  CategoryGroup,
} from '@miro-site/templates-design'

import checkUserInfo from '../utils/checkUserInfo'
import TemplatesSearch from './TemplatesSearch'
import {TemplateCardData} from '../data/mappers/toTemplateCard'
import {PreproductContextProvider, usePreproductPreviewContext} from '@miro-site/preproduct-preview'
export interface TemplatePageCategory {
  slug: string
  title: string
}

export interface TemplatePageProps {
  embedBoardKey: string
  metadataId: string
  accessConstraints?: {
    requiredPlans?: string[]
  }
  appSlug: string
  slug: string
  newBoardMetadataId: string
  headline: string
  description: string
  image: LazyImageProps
  wistiaVideoId?: string | null
  content: Document
  categories: TemplatePageCategory[]
  relatedTemplates?: TemplateCardData[]
  externalLinks?: TemplatePageExternalLink[]
  faq: FaqItemsProps
  categoryGroups: CategoryGroup[]
}

export interface TemplatePageExternalLink {
  title: string
  link: string
}

const toJsonLd = (items: FaqItem[]): string =>
  JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: [
        {
          '@type': 'Answer',
          text: item.answer,
        },
      ],
    })),
  })

const paidTemplatesDescriptions = [
  'paid-template-description-1',
  'paid-template-description-2',
  'paid-template-description-3',
]

const TemplatePage: React.FC<TemplatePageProps> = ({
  metadataId,
  newBoardMetadataId,
  embedBoardKey,
  headline,
  description,
  image,
  wistiaVideoId,
  relatedTemplates = [],
  categories = [],
  content = EMPTY_DOCUMENT,
  externalLinks = [],
  categoryGroups = [],
  faq,
  appSlug,
  accessConstraints,
}) => {
  const headerRef = useRef<HTMLElement>(null)
  const [metaId, setMetaId] = useState(null)
  const [templateId, setTemplateId] = useState(null)
  const {analytics} = useServicesContext()
  const {
    boardContainerState: {
      editableTemplates: {isEditableTemplatesExperiment},
    },
  } = usePreproductPreviewContext()

  const router = useRouter()

  const {t} = useTranslation('common')

  useEffect(() => {
    const autoOpen = typeof router.query['auto_open'] !== 'undefined'

    const {id: userId} = checkUserInfo() || {}

    if (autoOpen && userId) {
      handleAutoOpen()
    }

    async function handleAutoOpen() {
      await analytics.sendEvent(
        new Datalayer('template_auto_open', {
          userId,
          boardId: newBoardMetadataId,
        }),
      )

      setMetaId(newBoardMetadataId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newBoardMetadataId, router])

  const templateActions = {
    PREVIEW: t('templates-actions-preview'),
    USE_TEMPLATE: t('templates-actions-use-template'),
    SHOW_MORE_INFO: t('templates-actions-more-info'),
  }

  const paidTemplateDescription =
    accessConstraints?.requiredPlans?.length > 0
      ? t(paidTemplatesDescriptions[accessConstraints?.requiredPlans?.length - 1], {
          plan: accessConstraints?.requiredPlans[0],
          secondPlan: accessConstraints?.requiredPlans[1],
          thirdPlan: accessConstraints?.requiredPlans[2],
        })
      : null

  const stickyUseTemplateDesktopDescription = paidTemplateDescription
    ? `${t('get-started-text-paid')} ${paidTemplateDescription}`
    : t('get-started-text')

  const getActionButtonProps = ({
    appSlug,
    newBoardMetadataId,
    accessConstraints,
  }: TemplateCardData): TemplateCardProps['actionButtonProps'] => {
    if (accessConstraints?.requiredPlans?.length > 0) {
      return {
        children: t('templates-actions-open-template' as const),
        onClick: e => {
          e.preventDefault()
          setMetaId(null)
          setTemplateId(appSlug)
        },
      }
    }

    return {
      onClick: e => {
        e.preventDefault()
        setTemplateId(null)
        setMetaId(newBoardMetadataId)
      },
    }
  }

  const actionButtonProps = getActionButtonProps({
    appSlug,
    newBoardMetadataId,
    accessConstraints,
  } as TemplateCardData)

  return (
    <>
      <Head>
        {faq && faq.items.length > 0 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{__html: toJsonLd(faq.items)}}
          />
        )}
      </Head>
      <main data-selected-meta-id={metaId} data-selected-appslug={templateId}>
        <Box $mt={30} $mb={32}>
          <TemplatesSearch
            categoryGroups={categoryGroups}
            withBackButton={true}
            stickyFixingPoint={headerRef}
            isEditableTemplatesExperiment={isEditableTemplatesExperiment}
          />
        </Box>
        <Container>
          {isEditableTemplatesExperiment ? (
            <Template.EditableTemplateHeader
              metadataId={metadataId}
              templateActions={templateActions}
              ref={headerRef}
              headline={headline}
              onUseTemplateClick={actionButtonProps.onClick}
            />
          ) : (
            <Template.PageHeader
              embedBoardKey={embedBoardKey}
              metadataId={metadataId}
              templateActions={templateActions}
              ref={headerRef}
              headline={headline}
              description={description}
              paidTemplateDescription={paidTemplateDescription}
              image={image}
              wistiaVideoId={wistiaVideoId}
              onUseTemplateClick={actionButtonProps.onClick}
            />
          )}
          <Template.ContentSection
            description={description}
            paidTemplateDescription={paidTemplateDescription}
            isEditableTemplate={isEditableTemplatesExperiment}
            main={
              <>
                <RichText document={content} />
                {faq && <Template.FaqItems title={faq.title} items={faq.items} />}
              </>
            }
            aside={
              <>
                {categories.length > 0 && (
                  <Box $styles={{display: 'flex', flexWrap: 'wrap'}}>
                    {categories.map((c, i) => (
                      <Link key={c.title} href={c.slug} passHref legacyBehavior>
                        <Template.CategoryLink $mb={8} $mr={i < categories.length - 1 ? 8 : 0}>
                          {c.title}
                        </Template.CategoryLink>
                      </Link>
                    ))}
                  </Box>
                )}
                <Template.StickyUseTemplateDesktop
                  metadataId={metadataId}
                  description={stickyUseTemplateDesktopDescription}
                  templateActions={templateActions}
                  headline={headline}
                  actionButtonProps={actionButtonProps}
                />
                <Template.StickyUseTemplateMobile
                  metadataId={metadataId}
                  templateActions={templateActions}
                  showOnRefIntersection={headerRef}
                  actionButtonProps={actionButtonProps}
                />
              </>
            }
          />
        </Container>
        {relatedTemplates.length > 0 && (
          <CardGrid
            top={
              <Heading $variant={['h3', 'h2', 'h2', 'h2']}>
                {t('related-templates-headline' as const)}
              </Heading>
            }
          >
            {relatedTemplates.map(tpl => (
              <TemplateCard
                key={tpl.title}
                templateActions={templateActions}
                previewButton={defaultButton => (
                  <Link href={tpl.templatePagePath} passHref legacyBehavior>
                    {defaultButton}
                  </Link>
                )}
                actionButtonProps={getActionButtonProps(tpl)}
                {...tpl}
              />
            ))}
          </CardGrid>
        )}
        {externalLinks.length > 0 && (
          <CardGrid
            top={
              <Heading $variant={['h3', 'h2', 'h2', 'h2']} $mb={60}>
                {t('discover-more' as const)}
              </Heading>
            }
          >
            {externalLinks.map(l => (
              <ExternalLink key={l.title} {...l} />
            ))}
          </CardGrid>
        )}
      </main>
      <TemplateLoader metadataId={metaId} setMetadataId={setMetaId} />
      <TemplateOpener templateId={templateId} setTemplateId={setTemplateId} />
    </>
  )
}

const TemplatePageWrapper: React.FC<TemplatePageProps> = (props): React.ReactElement => {
  return (
    <PreproductContextProvider configType="editableTemplateBoard" templateKey={props.embedBoardKey}>
      <TemplatePage {...props} />
    </PreproductContextProvider>
  )
}

export default TemplatePageWrapper
